import React, { useState } from 'react';
import { supabase } from '../services/supabaseClient';

interface TransferOrgModalProps {
  org: any;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const TransferOrgModal: React.FC<TransferOrgModalProps> = ({ 
  org, 
  isOpen, 
  onClose,
  onSuccess 
}) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [clearTokens, setClearTokens] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<any>(null);

  if (!isOpen) return null;

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch('/api/orgs/transfer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          orgId: org.id,
          recipientEmail,
          clearTokens,
          transferNote: transferNote.trim() || null
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Transfer failed');
      }

      setSuccess(result);
      
      // Call success callback after a delay
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 3000);
      }

    } catch (err: any) {
      console.error('Transfer error:', err);
      setError(err.message || 'Failed to initiate transfer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900">Transfer Organization</h3>
          <p className="mt-1 text-sm text-gray-500">
            Transfer <span className="font-medium">{org.name}</span> to another user
          </p>
        </div>

        {success ? (
          <div className="mb-4">
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Transfer initiated!</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>{success.message}</p>
                    <p className="mt-1">Transfer expires: {new Date(success.expiresAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleTransfer} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Recipient Email
              </label>
              <input
                type="email"
                id="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="recipient@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="note" className="block text-sm font-medium text-gray-700">
                Transfer Note (optional)
              </label>
              <textarea
                id="note"
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                rows={3}
                placeholder="Any notes for the recipient..."
              />
            </div>

            <div className="flex items-center">
              <input
                id="clear-tokens"
                type="checkbox"
                checked={clearTokens}
                onChange={(e) => setClearTokens(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="clear-tokens" className="ml-2 block text-sm text-gray-900">
                Clear authentication tokens (recommended)
              </label>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> After transfer, the recipient will need to authenticate 
                with their own Salesforce credentials to sync this org.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !recipientEmail}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Initiating Transfer...' : 'Transfer Org'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};